import { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  Autocomplete,
  TextField,
  Chip,
  Tooltip,
  CircularProgress,
  LinearProgress,
  FormControl,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Switch,
  FormControlLabel,
  Paper,
} from '@mui/material';
import {
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next';
import api from '../../config/api';
import useStore from '../../config/store';

// Columnas fijas de la plantilla, en orden. El backend las reconoce por su
// nombre, en español o en inglés (utils/applicantImport.utils.js): si se
// cambia un texto de ats.import.template.columns, hay que agregarlo allá.
const TEMPLATE_FIELDS = [
  'names', 'firstLastName', 'secondLastName', 'email', 'phone', 'document',
  'nacionalidad', 'comuna', 'region', 'appliedAt', 'experiencia', 'formacion',
];
const REQUIRED_FIELDS = ['names', 'firstLastName', 'email'];
// Portales sugeridos (se puede escribir otro)
const ORIGIN_OPTIONS = ['Computrabajo', 'Laborum', 'Trabajando.com', 'Chiletrabajos', 'Bumeran', 'LinkedIn', 'Indeed'];
const MAX_ORIGIN_LENGTH = 60;
// Filas por llamada al backend (cada fila son varias lecturas y escrituras)
const BATCH_SIZE = 25;
// Filas de la vista previa por página
const ROWS_PER_PAGE = 100;
const FILE_ERRORS = ['FILE_REQUIRED', 'FILE_TOO_LARGE', 'UNREADABLE_FILE', 'EMPTY_FILE', 'NO_ROWS', 'FORBIDDEN'];

const STATUS_COLORS = {
  new: { bg: '#DCFCE7', color: '#166534' },
  existing: { bg: '#EDE9FE', color: '#4C1D95' },
  pending_validation: { bg: '#FEF3C7', color: '#92400E' },
  in_position: { bg: '#F3F4F6', color: '#374151' },
  error: { bg: '#FEE2E2', color: '#991B1B' },
};
// Fila que las reglas de la vacante descartan al importar (mismo color que el indicador "Descartadas")
const DISCARDED_COLORS = { bg: '#FEE2E2', color: '#991B1B' };

const COUNT_CHIPS = [
  { key: 'toImport', always: true, bg: '#DCFCE7', color: '#166534' },
  { key: 'new', always: true, bg: '#F3F4F6', color: '#374151' },
  { key: 'existing', always: true, bg: '#EDE9FE', color: '#4C1D95' },
  { key: 'pendingValidation', bg: '#FEF3C7', color: '#92400E' },
  { key: 'inPosition', bg: '#F3F4F6', color: '#374151' },
  { key: 'errors', bg: '#FEE2E2', color: '#991B1B' },
  { key: 'marked', bg: '#FEF3C7', color: '#92400E' },
  { key: 'discarded', bg: '#FEE2E2', color: '#991B1B' },
  { key: 'blocked', bg: '#E5E7EB', color: '#111827' },
];

// Nivel de revisión de una fila de la vista previa (null: sin marca)
const reviewLevel = (r) => {
  if (r.status === 'error') return 'error';
  if (r.status === 'in_position') return null;
  if (r.status === 'pending_validation' || r.blocked || r.discarded || (r.ruleMarks || []).length > 0
    || (r.warnings || []).some((w) => w.code !== 'NO_RUT')) return 'warning';
  return null;
};
// Fondo de la fila y barra a la izquierda (en la primera celda: el box-shadow
// de un <tr> no se dibuja en todos los navegadores)
const REVIEW_SX = {
  warning: { backgroundColor: '#FFFBEB', '& td:first-of-type': { boxShadow: 'inset 3px 0 0 #F59E0B' } },
  error: { backgroundColor: '#FEF2F2', '& td:first-of-type': { boxShadow: 'inset 3px 0 0 #EF4444' } },
};

const slugify = (s) =>
  String(s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);

/**
 * Importar postulantes a la vacante desde un archivo
 * (PLAN_IMPORTACION_COMPUTRABAJO, fase 1): plantilla de la vacante → portal de
 * origen y archivo → vista previa (conteos, vínculo de las preguntas y todas
 * las filas, con las que hay que revisar marcadas) → importación por tandas →
 * resumen con las filas no importadas. Sin mensajes automáticos. Solo se monta para quien puede editar
 * la vacante.
 *
 * Props: open, onClose, position (filteringQuestions y nombre), positionId,
 * onImported () => void (refrescar los candidatos).
 */
const AtsImportApplicantsDialog = ({ open, onClose, position, positionId, onImported }) => {
  const { t } = useTranslation();
  const ti = (key, opts) => t(`ats.import.${key}`, opts);

  // upload → preview → importing → done
  const [step, setStep] = useState('upload');
  const [origin, setOrigin] = useState('');
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [summary, setSummary] = useState(null);
  const [onlyReview, setOnlyReview] = useState(false);
  const [page, setPage] = useState(0);

  const questions = useMemo(
    () => (Array.isArray(position?.filteringQuestions) ? position.filteringQuestions : []).filter((q) => q && q.question),
    [position]
  );

  useEffect(() => {
    if (!open) return;
    setStep('upload');
    setOrigin('');
    setFile(null);
    setLoading(false);
    setError(null);
    setPreview(null);
    setMapping({});
    setProgress({ done: 0, total: 0 });
    setSummary(null);
    setOnlyReview(false);
  }, [open]);

  // Una vista previa nueva (otro archivo u otro vínculo) vuelve a la primera página
  useEffect(() => { setPage(0); }, [preview]);

  // Motivos y avisos llegan como códigos (el backend no traduce)
  const reasonText = (item, group) => t(`ats.import.${group}.${item.code}`, { ...item, defaultValue: item.code });

  const fileErrorText = (res) => {
    const type = res?.errorType;
    if (type === 'MISSING_COLUMNS') {
      const columns = (res.missing || []).map((f) => ti(`template.columns.${f}`)).join(', ');
      return ti('fileErrors.MISSING_COLUMNS', { columns });
    }
    if (FILE_ERRORS.includes(type)) return ti(`fileErrors.${type}`);
    return ti('fileErrors.generic');
  };

  // Plantilla de la vacante: columnas fijas + una por pregunta de filtro, y la
  // hoja "Instrucciones" con el formato y las respuestas válidas. Se genera
  // en el navegador (sin backend), como la de Campañas
  const downloadTemplate = () => {
    const columns = TEMPLATE_FIELDS.map((f) => ti(`template.columns.${f}`));
    const header = [...columns, ...questions.map((q) => q.question)];
    const sheet = XLSX.utils.aoa_to_sheet([header]);
    sheet['!cols'] = header.map((h) => ({ wch: Math.min(Math.max(String(h).length + 2, 14), 60) }));
    const formatOf = (q) => {
      if (q.type === 'yes_no') return ti('template.questionYesNo');
      if (q.type === 'multiple_choice' && Array.isArray(q.options) && q.options.length > 0) {
        return ti('template.questionOptions', { options: q.options.join(' / ') });
      }
      return ti('template.questionText');
    };
    const instructions = XLSX.utils.aoa_to_sheet([
      [ti('template.instructionsTitle', { position: position?.name || '' })],
      [],
      [ti('template.colColumn'), ti('template.colRequired'), ti('template.colFormat')],
      ...TEMPLATE_FIELDS.map((f, i) => [
        columns[i],
        REQUIRED_FIELDS.includes(f) ? ti('template.yes') : ti('template.no'),
        ti(`template.formats.${f}`),
      ]),
      ...questions.map((q) => [q.question, ti('template.no'), formatOf(q)]),
      [],
      [ti('template.note1')],
      [ti('template.note2')],
    ]);
    instructions['!cols'] = [{ wch: 45 }, { wch: 12 }, { wch: 90 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, ti('template.sheet'));
    XLSX.utils.book_append_sheet(wb, instructions, ti('template.instructionsSheet'));
    XLSX.writeFile(wb, `${ti('template.fileName')}_${slugify(position?.name) || positionId}.xlsx`);
  };

  const mappingOf = (data) =>
    Object.fromEntries(((data && data.questions) || []).map((q) => [String(q.id), q.columnIndex ?? null]));

  // Vista previa (no escribe nada). nextMapping: vínculo pregunta → columna
  // elegido por el usuario
  const runPreview = async (nextMapping = null) => {
    if (!file) return;
    const { authToken } = useStore.getState();
    const previous = preview;
    setLoading(true);
    setError(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('positionId', positionId);
      if (nextMapping) form.append('mapping', JSON.stringify(nextMapping));
      const { data } = await api.post('/applications/import/preview', form, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const res = data?.response;
      if (res?.status_response === 'OK' && res.data) {
        setPreview(res.data);
        setMapping(mappingOf(res.data));
        setStep('preview');
      } else {
        setError(fileErrorText(res));
        // El vínculo vuelve al de la vista previa vigente (lo que se importaría)
        if (previous) setMapping(mappingOf(previous));
      }
    } catch (err) {
      console.error('Error revisando el archivo de importación:', err);
      setError(fileErrorText(err.response?.data?.response));
      if (previous) setMapping(mappingOf(previous));
    } finally {
      setLoading(false);
    }
  };

  // La columna elegida pasa a esta pregunta (si la tenía otra, queda sin columna)
  const changeMapping = (questionId, value) => {
    const qid = String(questionId);
    const column = value === '' ? null : Number(value);
    const next = { ...mapping, [qid]: column };
    if (column !== null) {
      Object.keys(next).forEach((k) => { if (k !== qid && next[k] === column) next[k] = null; });
    }
    setMapping(next);
    runPreview(next);
  };

  const runImport = async () => {
    // Las que ya están en la vacante o tienen error no se mandan
    const rows = (preview?.rows || []).filter((r) => r.status !== 'error' && r.status !== 'in_position');
    const total = rows.length;
    if (total === 0) return;
    setStep('importing');
    setProgress({ done: 0, total });

    const results = [];
    let importId = null;
    let stopped = false;
    let processed = 0;
    for (let i = 0; i < total; i += BATCH_SIZE) {
      const batch = rows.slice(i, i + BATCH_SIZE);
      // Token vigente en cada tanda (una importación grande puede durar más que el token)
      const { authToken } = useStore.getState();
      try {
        const { data } = await api.post('/applications/import/commit', {
          positionId,
          origin: origin.trim(),
          importId,
          fileName: file?.name || null,
          totalRows: preview.counts.total,
          final: i + BATCH_SIZE >= total,
          // hints: las fichas que la vista previa encontró (el backend las verifica)
          rows: batch.map((r) => ({ row: r.row, raw: r.raw, hints: r.hints || [] })),
        }, { headers: { Authorization: `Bearer ${authToken}` } });
        const res = data?.response;
        if (res?.status_response !== 'OK' || !res.data) throw new Error('import batch failed');
        importId = res.data.importId;
        results.push(...(res.data.results || []));
        processed += batch.length;
        setProgress({ done: processed, total });
      } catch (err) {
        console.error('Error importando postulantes:', err);
        stopped = true;
        // Esta tanda y las siguientes no alcanzaron a importarse (o no se sabe)
        rows.slice(i).forEach((r) => results.push({ row: r.row, status: 'error', errors: [{ code: 'REQUEST_FAILED' }] }));
        break;
      }
    }

    const rawByRow = Object.fromEntries((preview?.rows || []).map((r) => [r.row, r.raw]));
    const count = (fn) => results.filter(fn).length;
    const imported = (r) => r.status === 'added' || r.status === 'pending_validation';
    const failed = [
      ...(preview?.rows || []).filter((r) => r.status === 'error').map((r) => ({ row: r.row, raw: r.raw, errors: r.errors || [] })),
      ...results.filter((r) => r.status === 'error').map((r) => ({ row: r.row, raw: rawByRow[r.row], errors: r.errors || [] })),
    ].sort((a, b) => (a.row || 0) - (b.row || 0));

    setSummary({
      added: count((r) => r.status === 'added' && !r.existing),
      existing: count((r) => r.status === 'added' && r.existing),
      pending: count((r) => r.status === 'pending_validation'),
      inPosition: (preview?.counts?.inPosition || 0) + count((r) => r.status === 'in_position'),
      errors: failed.length,
      marked: count((r) => imported(r) && r.marked),
      discarded: count((r) => imported(r) && r.discarded),
      blocked: count((r) => imported(r) && r.blocked),
      failed,
      stopped,
      processed,
      total,
    });
    setStep('done');
    if (onImported) onImported();
  };

  // Filas no importadas en el formato de la plantilla (más la fila y el
  // motivo): se corrigen y se vuelven a importar en el mismo archivo
  const downloadFailed = () => {
    if (!summary) return;
    const header = [
      ti('errorsFile.row'),
      ...TEMPLATE_FIELDS.map((f) => ti(`template.columns.${f}`)),
      ...questions.map((q) => q.question),
      ti('errorsFile.reason'),
    ];
    const lines = summary.failed.map((r) => [
      r.row,
      ...TEMPLATE_FIELDS.map((f) => (r.raw && r.raw[f]) || ''),
      ...questions.map((q) => (r.raw && r.raw.answers && r.raw.answers[String(q.id)]) || ''),
      r.errors.map((e) => reasonText(e, 'errors')).join(' · '),
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([header, ...lines]), ti('errorsFile.sheet'));
    XLSX.writeFile(wb, ti('errorsFile.name'));
  };

  // Todas las filas del archivo; se marcan las que hay que revisar: 'error' (no
  // se importan, en rojo) y 'warning' (entran con algo que mirar, en amarillo).
  // "Ya en la vacante" no se marca: no se importa ni se toca.
  const fileRows = useMemo(() => preview?.rows || [], [preview]);
  const reviewRows = useMemo(() => fileRows.filter((r) => reviewLevel(r)), [fileRows]);
  const shownRows = onlyReview ? reviewRows : fileRows;
  const pageRows = shownRows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  const noRutCount = useMemo(() => (preview?.rows || []).filter((r) =>
    r.status !== 'error' && r.status !== 'in_position'
    && (r.warnings || []).some((w) => w.code === 'NO_RUT')).length, [preview]);

  const detailOf = (r) => [
    ...(r.errors || []).map((e) => reasonText(e, 'errors')),
    ...(r.warnings || []).filter((w) => w.code !== 'NO_RUT').map((w) => reasonText(w, 'warnings')),
    ...(r.ruleMarks || []),
    ...(r.blocked ? [ti('blockedRow')] : []),
    ...(r.status !== 'error' && r.status !== 'in_position' && (r.warnings || []).some((w) => w.code === 'NO_RUT')
      ? [ti('noRutRow')] : []),
  ].join(' · ');

  // Columnas que se pueden vincular a una pregunta: todas menos las de los
  // campos obligatorios (una opcional pasa a la pregunta)
  const selectableColumns = (preview?.columns || []).filter((c) => !REQUIRED_FIELDS.includes(c.field));

  // Preguntas sin columna en el vínculo actual: se avisa para que "Sin
  // columna" no parezca un paso pendiente (esas respuestas no se importan)
  const unlinkedCount = (preview?.questions || []).filter((q) => {
    const column = mapping[String(q.id)];
    return column === null || column === undefined || column === '';
  }).length;

  const busy = loading || step === 'importing';
  const purple = { backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' } };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle>{ti('title')}</DialogTitle>
      <DialogContent dividers>

        {step === 'upload' && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            <Typography variant="body2" color="text.secondary">{ti('description')}</Typography>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>{ti('templateStep')}</Typography>
              <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
                {ti('downloadTemplate')}
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {ti('templateHelp')}
              </Typography>
            </Box>
            <Box>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>{ti('fileStep')}</Typography>
              <Autocomplete
                freeSolo
                options={ORIGIN_OPTIONS}
                inputValue={origin}
                onInputChange={(_, value) => setOrigin(String(value || '').slice(0, MAX_ORIGIN_LENGTH))}
                renderInput={(params) => (
                  <TextField {...params} size="small" label={ti('originLabel')} helperText={ti('originHelp')} />
                )}
                sx={{ mb: 2, maxWidth: 360 }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <Button component="label" variant="outlined" size="small" startIcon={<UploadFileIcon />}>
                  {ti('chooseFile')}
                  <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    data-testid="import-file-input"
                    onChange={(e) => {
                      setFile(e.target.files?.[0] || null);
                      setError(null);
                      e.target.value = '';
                    }}
                  />
                </Button>
                {file && <Typography variant="body2">{file.name}</Typography>}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                {ti('fileHelp')}
              </Typography>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {step === 'preview' && preview && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body2" fontWeight={700}>{ti('previewTitle', { file: file?.name || '' })}</Typography>
              {loading && <CircularProgress size={16} />}
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {/* Al pasar el mouse, cada indicador explica qué significa */}
              {COUNT_CHIPS.filter((c) => c.always || (preview.counts?.[c.key] || 0) > 0).map((c) => (
                <Tooltip key={c.key} title={ti(`countsHelp.${c.key}`)} describeChild>
                  <Chip
                    size="small"
                    label={ti(`counts.${c.key}`, { count: preview.counts?.[c.key] || 0 })}
                    sx={{ backgroundColor: c.bg, color: c.color, fontWeight: 700 }}
                  />
                </Tooltip>
              ))}
            </Box>
            {noRutCount > 0 && (
              <Typography variant="caption" color="text.secondary">{ti('noRut', { count: noRutCount })}</Typography>
            )}
            {(preview.warnings || []).length > 0 && (
              <Alert severity="warning">
                {preview.warnings.map((w) => (
                  <Typography key={w.code} variant="body2">{reasonText(w, 'warnings')}</Typography>
                ))}
              </Alert>
            )}
            {(preview.ignoredColumns || []).length > 0 && (
              <Typography variant="caption" color="text.secondary">
                {ti('ignoredColumns', { columns: preview.ignoredColumns.map((c) => c.header).join(', ') })}
              </Typography>
            )}

            {(preview.questions || []).length > 0 && (
              <Box>
                <Typography variant="body2" fontWeight={600}>{ti('questionsTitle')}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {ti('questionsHelp')}
                </Typography>
                {unlinkedCount > 0 && (
                  <Alert severity="info" sx={{ mb: 1 }}>
                    {unlinkedCount === preview.questions.length
                      ? ti('questionsNoneLinked')
                      : ti('questionsSomeUnlinked', { count: unlinkedCount })}
                  </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {preview.questions.map((q) => (
                    <Box key={q.id} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>{q.question}</Typography>
                      <FormControl size="small" sx={{ width: 260, flexShrink: 0 }}>
                        <Select
                          value={mapping[String(q.id)] ?? ''}
                          displayEmpty
                          disabled={loading}
                          onChange={(e) => changeMapping(q.id, e.target.value)}
                          inputProps={{ 'aria-label': q.question }}
                        >
                          <MenuItem value=""><em>{ti('noColumn')}</em></MenuItem>
                          {/* Una columna de datos (Experiencia, Comuna…) se puede usar
                              para la pregunta, y ese dato deja de importarse */}
                          {selectableColumns.map((c) => (
                            <MenuItem key={c.index} value={c.index}>
                              {c.field ? ti('fieldColumnOption', { header: c.header }) : c.header}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {fileRows.length > 0 && (
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                  <Typography variant="body2" fontWeight={600}>{ti('rowsTitle', { count: fileRows.length })}</Typography>
                  {reviewRows.length > 0 && (
                    <FormControlLabel
                      control={(
                        <Switch
                          size="small"
                          checked={onlyReview}
                          onChange={(e) => { setOnlyReview(e.target.checked); setPage(0); }}
                        />
                      )}
                      label={<Typography variant="body2">{ti('onlyReview', { count: reviewRows.length })}</Typography>}
                      sx={{ mr: 0 }}
                    />
                  )}
                </Box>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>{ti('table.row')}</TableCell>
                        <TableCell>{ti('table.person')}</TableCell>
                        <TableCell>{ti('table.status')}</TableCell>
                        <TableCell>{ti('table.detail')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {pageRows.map((r) => {
                        const colors = STATUS_COLORS[r.status] || STATUS_COLORS.in_position;
                        const level = reviewLevel(r);
                        return (
                          <TableRow key={r.row} data-review={level || undefined} sx={level ? REVIEW_SX[level] : undefined}>
                            <TableCell>{r.row}</TableCell>
                            <TableCell>
                              <Typography variant="body2">{r.name || r.email || '—'}</Typography>
                              {r.applicantName && r.applicantName !== r.name && (
                                <Typography variant="caption" color="text.secondary">→ {r.applicantName}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={ti(`status.${r.status}`)}
                                sx={{ backgroundColor: colors.bg, color: colors.color, fontWeight: 600 }}
                              />
                              {/* Entra, pero las reglas la dejan en Descartados; el motivo va en el detalle (2026-10-08) */}
                              {r.discarded && (
                                <Chip
                                  size="small"
                                  label={ti('discardedRow')}
                                  sx={{ ml: 0.5, backgroundColor: DISCARDED_COLORS.bg, color: DISCARDED_COLORS.color, fontWeight: 600 }}
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption">{detailOf(r)}</Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
                {shownRows.length > ROWS_PER_PAGE && (
                  <TablePagination
                    component="div"
                    count={shownRows.length}
                    page={page}
                    onPageChange={(e, next) => setPage(next)}
                    rowsPerPage={ROWS_PER_PAGE}
                    rowsPerPageOptions={[]}
                    labelDisplayedRows={({ from, to, count }) => ti('table.pageRange', { from, to, count })}
                    getItemAriaLabel={(type) => ti(type === 'next' ? 'table.nextPage' : 'table.previousPage')}
                  />
                )}
              </Box>
            )}
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {step === 'importing' && (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" sx={{ mb: 1.5 }}>
              {ti('importing', { done: progress.done, total: progress.total })}
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progress.total ? (progress.done * 100) / progress.total : 0}
            />
          </Box>
        )}

        {step === 'done' && summary && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Alert severity={summary.stopped ? 'error' : (summary.errors > 0 ? 'warning' : 'success')}>
              <Typography variant="body2" fontWeight={700}>{ti('doneTitle')}</Typography>
              <Typography variant="body2">
                {ti('doneSummary', {
                  added: summary.added,
                  existing: summary.existing,
                  pending: summary.pending,
                  inPosition: summary.inPosition,
                  errors: summary.errors,
                })}
              </Typography>
              {(summary.marked + summary.discarded + summary.blocked) > 0 && (
                <Typography variant="body2">
                  {ti('doneMarks', { marked: summary.marked, discarded: summary.discarded, blocked: summary.blocked })}
                </Typography>
              )}
            </Alert>
            {summary.stopped && (
              <Alert severity="error">{ti('stopped', { done: summary.processed, total: summary.total })}</Alert>
            )}
            {summary.failed.length > 0 && (
              <Box>
                <Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={downloadFailed}>
                  {ti('downloadErrors')}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'upload' && (
          <>
            <Button onClick={onClose} disabled={loading}>{ti('cancel')}</Button>
            <Button
              variant="contained"
              onClick={() => runPreview()}
              disabled={loading || !file || !origin.trim()}
              sx={purple}
            >
              {loading ? <CircularProgress size={20} color="inherit" /> : ti('review')}
            </Button>
          </>
        )}
        {step === 'preview' && (
          <>
            <Button
              onClick={() => { setStep('upload'); setPreview(null); setError(null); }}
              disabled={loading}
            >
              {ti('back')}
            </Button>
            <Button
              variant="contained"
              onClick={runImport}
              disabled={loading || !preview || (preview.counts?.toImport || 0) === 0}
              sx={purple}
            >
              {ti('importButton', { count: preview?.counts?.toImport || 0 })}
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button variant="contained" onClick={onClose} sx={purple}>{ti('close')}</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AtsImportApplicantsDialog;
